/**
 * TrialDemoBanner — Persistent but non-intrusive banner shown when
 * the trial user is in demo data mode. Provides a clear "Start with
 * Your Real Organization" CTA to transition into real setup.
 */
import { useState } from "react";
import { Database, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTrialOrientation } from "@/hooks/useTrialOrientation";
import TrialPurgeDemoDialog from "@/components/trial/TrialPurgeDemoDialog";

export default function TrialDemoBanner() {
  const { isDemoMode } = useTrialOrientation();
  const [dismissed, setDismissed] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);

  if (!isDemoMode || dismissed) return null;

  return (
    <>
      <div className="bg-accent/50 border-b border-border px-4 py-2">
        <div className="flex items-center justify-between gap-3 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 min-w-0">
            <Database className="w-3.5 h-3.5 text-primary shrink-0" />
            <p className="text-xs text-muted-foreground truncate">
              <span className="font-medium text-foreground">Demo Mode</span> — You're exploring with sample data.
              Everything here is safe to click.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setPurgeOpen(true)}>
              Start with Your Real Org
              <ArrowRight className="w-3 h-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => setDismissed(true)}
              aria-label="Dismiss demo banner"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <TrialPurgeDemoDialog open={purgeOpen} onOpenChange={setPurgeOpen} />
    </>
  );
}
